import { Box } from "@mui/material";

import {
  AgeCategory,
  Discipline,
  Ligue,
  WeightCategory,
} from "@prisma-remote/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AthletePatch,
  AthleteWithAll,
  athleteWithAllToAthleteModel,
  getCurrentAthlete,
  recalculateMeetRankings,
  GroupPatch,
  GroupWithAll,
  groupWithAllToGroupModel,
  MeetPatch,
  MeetPayload,
  MeetWithAll,
  SyncMeta,
  SyncResult,
  ThemeColors,
} from "../../../util/src";
import WebsocketClient from "../../../util/src/websocket-client";

interface CurrentGroupWidgetProps {
  serverUrl?: string;
  meetId: string;
  token?: string;
  colors: ThemeColors;
  theme?: string;
}

const CurrentGroupWidget: React.FC<CurrentGroupWidgetProps> = ({
  serverUrl,
  meetId,
  token,
  colors,
}) => {
  const [currentMeet, setCurrentMeet] = useState<MeetWithAll | null>(null);
  const [currentGroup, setCurrentGroup] = useState<GroupWithAll | null>(null);
  const ageCategories = useRef<AgeCategory[]>([]);
  const weightCategories = useRef<WeightCategory[]>([]);

  const currentMeetRef = useRef<MeetWithAll | null>(null);

  useEffect(() => {
    currentMeetRef.current = currentMeet;
  }, [currentMeet]);

  const disciplines = useRef<Discipline[]>([]);
  const ligues = useRef<Ligue[]>([]);

  const syncMetaRef = useRef<SyncMeta | null>(null);
  const resyncingRef = useRef(false);

  const meetPayload: MeetPayload | null = useMemo(() => {
    if (!meetId) return null;
    return token ? { id: meetId, auth_token: token } : { id: meetId };
  }, [meetId, token]);

  const shouldApply = useCallback(
    (r: SyncResult) => r === "SNAPSHOT" || r === "REVISION",
    [],
  );

  const requestResync = useCallback(
    (wsClient: WebsocketClient) => {
      if (!meetPayload) return;
      if (resyncingRef.current) return;
      resyncingRef.current = true;
      wsClient.emit("meet", meetPayload);
    },
    [meetPayload],
  );

  const checkSyncMeta = useCallback(
    (syncMeta: SyncMeta, wsClient: WebsocketClient): SyncResult => {
      if (!meetPayload) return "RESYNC";

      if (resyncingRef.current) {
        requestResync(wsClient);
        return "RESYNC";
      }

      const last = syncMetaRef.current;

      // session change
      if (!last || syncMeta.sessionId !== last.sessionId) {
        if (syncMeta.rev === 0) {
          syncMetaRef.current = syncMeta;
          return "SNAPSHOT";
        }
        requestResync(wsClient);
        return "RESYNC";
      }

      // duplicate / old
      if (syncMeta.rev <= last.rev) return "DUPLICATE";

      // perfect next
      if (syncMeta.rev === last.rev + 1) {
        syncMetaRef.current = syncMeta;
        return "REVISION";
      }

      // gap
      requestResync(wsClient);
      return "RESYNC";
    },
    [meetPayload, requestResync],
  );

  const acceptSnapshot = useCallback((syncMeta: SyncMeta) => {
    const last = syncMetaRef.current;
    if (
      last &&
      syncMeta.sessionId === last.sessionId &&
      syncMeta.rev <= last.rev
    )
      return false;

    syncMetaRef.current = syncMeta;
    resyncingRef.current = false;
    return true;
  }, []);

  useEffect(() => {
    const getLigue = (id: number) => {
      if (ligues.current) return ligues.current.find((l) => l.id === id);
    };

    const getDiscipline = (id: number) => {
      if (disciplines.current)
        return disciplines.current.find((d) => d.id === id);
    };

    const getAgeCategory = (id: number) => {
      if (ageCategories.current)
        return ageCategories.current.find((a) => a.id === id);
    };

    const getWeightCategory = (id: number) => {
      if (weightCategories.current)
        return weightCategories.current.find((w) => w.id === id);
    };
    const patchMeet = async (
      patch: AthletePatch[] | GroupPatch | MeetPatch,
    ) => {
      let connectionId: number;
      if ("length" in patch) {
        //AthletePatch
        const meet = currentMeetRef.current;
        if (!meet) return;
        for (const p of patch) {
          const targetGroupId = p.data.group?.connect?.id;
          if (targetGroupId) {
            const athlete = meet.groups
              .flatMap((g) => g.athletes)
              .find((a) => a.id === p.id);

            if (athlete) {
              const currentGroup = meet.groups.find(
                (g) => g.id === athlete?.group_id,
              );
              const newGroup = meet.groups.find((g) => g.id === targetGroupId);
              if (currentGroup && newGroup) {
                currentGroup.athletes = currentGroup.athletes.filter(
                  (a) => a.id !== p.id,
                );

                athlete.group_id = targetGroupId;

                if (!newGroup.athletes.some((a) => a.id === p.id)) {
                  newGroup.athletes = [...newGroup.athletes, athlete];
                }
              }
            }
          }
        }

        await Promise.all(
          patch.map((pat) => {
            const group = meet?.groups.find((g) => g.id === pat.groupId);
            if (!group) return;
            const athlete = group.athletes.find((a) => a.id === pat.id);
            if (!athlete) return;
            if ("weightCategory" in pat.data) {
              connectionId = pat.data.weightCategory?.connect?.id as number;
              Object.assign(athlete, {
                weightCategory: getWeightCategory(connectionId),
                weightCategoryId: connectionId,
              });
              delete pat.data.weightCategory;
            }
            if ("ageCategory" in pat.data) {
              connectionId = pat.data.ageCategory?.connect?.id as number;
              Object.assign(athlete, {
                ageCategory: getAgeCategory(connectionId),
                ageCategoryId: connectionId,
              });
              delete pat.data.ageCategory;
            }
            if ("discipline" in pat.data) {
              connectionId = pat.data.discipline?.connect?.id as number;
              Object.assign(athlete, {
                discipline: getDiscipline(connectionId),
                disciplineId: connectionId,
              });
              delete pat.data.discipline;
            }

            Object.assign(athlete, pat.data);
          }),
        );
        setCurrentMeet(JSON.parse(JSON.stringify(meet)));
        setCurrentGroup(
          JSON.parse(
            JSON.stringify(meet.groups.find((g) => g.selected === true)),
          ),
        );
      } else if ("meetId" in patch) {
        //GroupPatch
        const meet = currentMeetRef.current;
        if (!meet) return;
        const group = meet?.groups.find((g) => g.id === patch.id);

        if (!group) return;

        Object.assign(group, patch.data);
        setCurrentMeet(JSON.parse(JSON.stringify(meet)));
        setCurrentGroup(
          JSON.parse(
            JSON.stringify(meet.groups.find((g) => g.selected === true)),
          ),
        );
      } else {
        //MeetPatch
        const meet = currentMeetRef.current;
        if (!meet) return;
        if ("ligue" in patch.data) {
          connectionId = patch.data.ligue?.connect?.id as number;
          Object.assign(meet, {
            ligue: getLigue(connectionId),
            ligueId: connectionId,
          });
          delete patch.data.ligue;
        }
        Object.assign(meet, patch.data);
        if (typeof patch.data.isNational === "boolean")
          recalculateMeetRankings(meet);
        setCurrentMeet(JSON.parse(JSON.stringify(meet)));
        setCurrentGroup(
          JSON.parse(
            JSON.stringify(meet.groups.find((g) => g.selected === true)),
          ),
        );
      }
    };
    if (meetId) {
      const onceConnected = (wsClient: WebsocketClient) => {
        if (wsClient.isConnected()) {
          wsClient.off(meetId);
          wsClient.off(`${meetId}/deps`);
          wsClient.off(`${meetId}/select-group`);
          wsClient.off(`${meetId}/athlete-delete`);
          wsClient.off(`${meetId}/group-add`);
          wsClient.off(`${meetId}/group-delete`);
          wsClient.off(`${meetId}/athlete-add`);
          wsClient.off(`${meetId}/meet-patch`);
          wsClient.off(`${meetId}/athlete-patch`);
          wsClient.off(`${meetId}/group-patch`);
          wsClient.on(meetId, (data) => {
            const { payload, syncMeta } = data as {
              payload: MeetWithAll;
              syncMeta: SyncMeta;
            };
            if (!acceptSnapshot(syncMeta)) return;
            setCurrentMeet(payload);
            setCurrentGroup(
              payload.groups.find((g) => g.selected === true) ?? null,
            );
          });
          wsClient.on(`${meetId}/deps`, (data) => {
            const deps = data as {
              ligues: Ligue[];
              ageCategories: AgeCategory[];
              weightCategories: WeightCategory[];
              disciplines: Discipline[];
            };
            ligues.current = deps.ligues;
            weightCategories.current = deps.weightCategories;
            ageCategories.current = deps.ageCategories;
            disciplines.current = deps.disciplines;
          });
          wsClient.on(`${meetId}/select-group`, async (data) => {
            const { payload, syncMeta } = data as {
              payload: { groupId: string };
              syncMeta: SyncMeta;
            };
            const r = checkSyncMeta(syncMeta, wsClient);
            if (!shouldApply(r)) return;
            const meet = currentMeetRef.current;
            if (meet) {
              await Promise.all(
                meet.groups.map((group) => {
                  if (group.id === payload.groupId) {
                    Object.assign(group, { selected: true });
                  } else {
                    Object.assign(group, { selected: false });
                  }
                }),
              );
              setCurrentMeet(meet);
              setCurrentGroup(
                meet.groups.find((g) => g.id === payload.groupId) ?? null,
              );
            }
          });
          wsClient.on(`${meetId}/athlete-delete`, (data) => {
            const { payload, syncMeta } = data as {
              payload: {
                athleteId: string;
                groupId: string;
              };
              syncMeta: SyncMeta;
            };
            const r = checkSyncMeta(syncMeta, wsClient);
            if (!shouldApply(r)) return;
            const meet = currentMeetRef.current;
            if (meet) {
              const group = meet.groups.find((g) => g.id === payload.groupId);
              if (group) {
                group.athletes = group.athletes.filter(
                  (a) => a.id !== payload.athleteId,
                );
                setCurrentMeet(JSON.parse(JSON.stringify(meet)));
                if (group.selected)
                  setCurrentGroup(JSON.parse(JSON.stringify(group)));
              }
            }
          });
          wsClient.on(`${meetId}/group-add`, (data) => {
            const { payload, syncMeta } = data as {
              payload: GroupWithAll;
              syncMeta: SyncMeta;
            };
            const r = checkSyncMeta(syncMeta, wsClient);
            if (!shouldApply(r)) return;
            const meet = currentMeetRef.current;
            if (meet) {
              meet.groups.push(payload);
              setCurrentMeet(JSON.parse(JSON.stringify(meet)));
            }
          });
          wsClient.on(`${meetId}/group-delete`, (data) => {
            const { payload, syncMeta } = data as {
              payload: string;
              syncMeta: SyncMeta;
            };
            const r = checkSyncMeta(syncMeta, wsClient);
            if (!shouldApply(r)) return;
            const meet = currentMeetRef.current;
            if (meet) {
              meet.groups = meet.groups.filter((g) => g.id !== payload);
              setCurrentMeet(JSON.parse(JSON.stringify(meet)));
            }
          });
          wsClient.on(`${meetId}/athlete-add`, (data) => {
            const { payload, syncMeta } = data as {
              payload: AthleteWithAll;
              syncMeta: SyncMeta;
            };
            const r = checkSyncMeta(syncMeta, wsClient);
            if (!shouldApply(r)) return;
            const meet = currentMeetRef.current;
            if (meet) {
              const group = meet.groups.find((g) => g.id === payload.group_id);
              if (group) {
                group.athletes.push(payload);
                setCurrentMeet(JSON.parse(JSON.stringify(meet)));
                if (group.selected)
                  setCurrentGroup(JSON.parse(JSON.stringify(group)));
              }
            }
          });
          wsClient.on(`${meetId}/meet-patch`, (data) => {
            const { payload, syncMeta } = data as {
              payload: MeetPatch;
              syncMeta: SyncMeta;
            };
            const r = checkSyncMeta(syncMeta, wsClient);
            if (!shouldApply(r)) return;
            patchMeet(payload);
          });
          wsClient.on(`${meetId}/athlete-patch`, async (data) => {
            const { payload, syncMeta } = data as {
              payload: AthletePatch[];
              syncMeta: SyncMeta;
            };
            const r = checkSyncMeta(syncMeta, wsClient);
            if (!shouldApply(r)) return;
            await patchMeet(payload);
          });
          wsClient.on(`${meetId}/group-patch`, (data) => {
            const { payload, syncMeta } = data as {
              payload: GroupPatch;
              syncMeta: SyncMeta;
            };
            const r = checkSyncMeta(syncMeta, wsClient);
            if (!shouldApply(r)) return;
            patchMeet(payload);
          });
          wsClient.emit("meet", { id: meetId, auth_token: token });
        }
      };
      if (serverUrl) {
        const wsClient = WebsocketClient.getInstance(serverUrl);
        if (!wsClient.isConnected()) {
          wsClient.connect(() => onceConnected(wsClient));
        } else {
          onceConnected(wsClient);
        }
        return () => {
          wsClient.off(meetId);
          wsClient.off(`${meetId}/deps`);
          wsClient.off(`${meetId}/select-group`);
          wsClient.off(`${meetId}/athlete-delete`);
          wsClient.off(`${meetId}/group-add`);
          wsClient.off(`${meetId}/group-delete`);
          wsClient.off(`${meetId}/athlete-add`);
          wsClient.off(`${meetId}/meet-patch`);
          wsClient.off(`${meetId}/athlete-patch`);
          wsClient.off(`${meetId}/group-patch`);
        };
      }
    }
  }, [meetId, token, serverUrl, acceptSnapshot, checkSyncMeta, shouldApply]);

  if (
    !currentMeet ||
    !currentGroup ||
    !disciplines.current ||
    !ligues.current ||
    !ageCategories.current ||
    !weightCategories.current
  )
    return null;

  const currentAthletes = currentGroup.athletes;
  const currentAthlete = getCurrentAthlete(
    currentAthletes.length > 0
      ? currentAthletes.map((a) => athleteWithAllToAthleteModel(a))
      : [],
  );

  return (
    <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
      <ReadOnlyRankingTableOld
        athletes={currentMeet.groups.flatMap((group) =>
          group.athletes.map((a) => athleteWithAllToAthleteModel(a)),
        )}
        sortByLiveWeight
        colors={colors}
        currentGroup={groupWithAllToGroupModel(currentGroup)}
        currentAthlete={currentAthlete ?? null}
        records={currentMeet.records ?? []}
        maxHeight="90vh"
        levels={currentMeet.niveaux ?? []}
        pointsFormula={currentMeet.pointsFormula}
        isNational={currentMeet.isNational}
      />
    </Box>
  );
};

import PropTypes from "prop-types";
import ReadOnlyRankingTableOld from "../ReadOnlyRankingTable";

CurrentGroupWidget.propTypes = {
  meetId: PropTypes.string.isRequired,
  serverUrl: PropTypes.string,
  token: PropTypes.string,
  colors: PropTypes.any.isRequired,
};

export default CurrentGroupWidget;
